import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, DatePicker, Space, Button, Tag, Tooltip, Dropdown, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { Report, StatusBar } from '../components/reportVisuals';
import { formatDateTime } from '../utils/datetime';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// The list only lists. A single report is a page of its own (/reports/:id) —
// it holds every server with its components and per-target attempts, which at
// ~250 servers is something you link to and filter, not something you open in
// a modal on top of this table.

const ReportsPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(pageSize) };
      if (dateRange) {
        params.dateFrom = dateRange[0];
        params.dateTo = dateRange[1];
      }
      const { data } = await apiClient.get('/reports', { params });
      setReports(data.data.items || []);
      setTotal(data.data.total);
    } catch {
      message.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, dateRange, message]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await apiClient.post('/reports/generate');
      message.success('Report generated');
      fetchReports();
    } catch {
      message.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const exportReport = async (id: string, format: 'csv' | 'json') => {
    try {
      const { data } = await apiClient.get(`/reports/${id}/export?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${id}.${format}`;
      link.click();
    } catch {
      message.error('Failed to export report');
    }
  };

  const columns: ColumnsType<Report> = [
    {
      // `date` is the generator's own UTC stamp; `generatedAt` is the same
      // moment as RFC3339. Both are shown on the operator's clock — a report
      // named "14:30" that landed at 16:30 their time is a report they cannot
      // match to the outage they are looking at.
      //
      // Reports land seconds apart, so the seconds stay in the cell; what goes
      // is the wrapping — a two-line timestamp doubles every row on this page.
      title: 'Date', dataIndex: 'date', key: 'date', width: 170,
      ellipsis: { showTitle: false },
      render: (d: string, r: Report) => (
        <Link to={`/reports/${r.id}`} style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          {formatDateTime(r.generatedAt || d, d)}
        </Link>
      ),
    },
    {
      title: 'Networks', key: 'netHealth', width: 190,
      render: (_: unknown, r: Report) => <StatusBar breakdown={r.summary?.networks} />,
    },
    {
      title: 'Proxies', key: 'proxyHealth', width: 190,
      render: (_: unknown, r: Report) => <StatusBar breakdown={r.summary?.proxies} />,
    },
    {
      // One line, worst two issues, the rest behind the hover — the cell used
      // to wrap three tags and set the height of the whole table.
      title: 'Top Issues', key: 'issues',
      render: (_: unknown, r: Report) => {
        const issues = r.topIssues || [];
        if (issues.length === 0) return <Text type="secondary">--</Text>;
        return (
          <Tooltip
            title={
              <div style={{ fontSize: 12 }}>
                {issues.map((issue, i) => (
                  <div key={i}>{issue.errorType} · {issue.country}: {issue.count}</div>
                ))}
              </div>
            }
          >
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', overflow: 'hidden' }}>
              {issues.slice(0, 2).map((issue, i) => (
                // Neutral tag: the two bars to the left already carry the
                // status colour, and a row of orange tags beside them drowned
                // the bars out. The count leads so it can be scanned down.
                <Tag
                  key={i}
                  style={{
                    margin: 0, fontSize: 12, lineHeight: '20px',
                    maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  <Text strong style={{ fontSize: 12 }}>{issue.count}</Text> {issue.errorType} · {issue.country}
                </Tag>
              ))}
              {issues.length > 2 && (
                <Text type="secondary" style={{ fontSize: 12 }}>+{issues.length - 2}</Text>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      // Two icon buttons instead of three: CSV and JSON are one decision
      // ("download it"), so they share a menu and give their 80px to Top Issues.
      title: 'Actions', key: 'actions', width: 76,
      render: (_: unknown, r: Report) => (
        <Space size={4}>
          <Tooltip title="Open report">
            <Button size="small" aria-label="Open report" icon={<EyeOutlined />} onClick={() => navigate(`/reports/${r.id}`)} />
          </Tooltip>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'csv', label: 'Download CSV' },
                { key: 'json', label: 'Download JSON' },
              ],
              onClick: ({ key }) => exportReport(r.id, key as 'csv' | 'json'),
            }}
          >
            <Tooltip title="Download">
              <Button size="small" aria-label="Download report" icon={<DownloadOutlined />} />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Title and the two controls share one line: on a 1280×800 screen a
          filter row of its own costs a row of data. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        flexWrap: 'wrap', marginBottom: 10,
      }}>
        <Title level={4} style={{ margin: 0 }}>Reports</Title>
        <div style={{ flex: 1 }} />
        <RangePicker
          size="small"
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
            } else {
              setDateRange(null);
            }
            setPage(1);
          }}
        />
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleGenerate} loading={generating}>
          Generate Report
        </Button>
      </div>

      <Table
        dataSource={reports}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: page,
          total,
          pageSize,
          size: 'small',
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
        }}
      />
    </>
  );
};

export default ReportsPage;
